import {parseISO} from 'date-fns';
import {Card, Form, FormSubmitHandler} from 'platform/components';
import {Hide, Show, VStack} from 'platform/foundation';

import {DeepPartial} from 'react-hook-form';

import {isNotNil} from 'ramda-adjunct';

import {InvoicingDocumentType, RelatedDocumentForCalculationRequestBody} from '@dms/api/finance';
import {SupplierResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {getApiDateString, Nullish} from 'shared/web';

import {InvoiceVatRateProvider} from '../context/InvoiceVatRateProvider';
import {AccountingDocumentFormValues} from '../types';
import {createCorrectiveTaxDocumentSchema} from '../utils/createCorrectiveTaxDocumentSchema';
import {CorrectiveTaxDocumentDetails} from './CorrectiveTaxDocumentDetails';
import {CustomerAndSupplier} from './CustomerAndSupplier';
import {DocumentSeriesSelect} from './DocumentSeriesSelect';
import {FormHeader} from './FormHeader';
import {InvoicePage} from './InvoicePage';
import {ListOfItemsForm} from './listOfItems/ListOfItemsForm';
import {PaymentDates} from './PaymentDates';
import {PaymentDetails} from './PaymentDetails';
import {PaymentsNotes} from './PaymentsNotes';

interface CreateCorrectiveTaxDocumentFormProps {
  defaultValues: DeepPartial<AccountingDocumentFormValues>;
  handleFormSubmit: FormSubmitHandler<AccountingDocumentFormValues>;
  dateOfTaxableSupply?: string;
  supplier?: SupplierResponseBody;
  isListOfItemsDisabled?: boolean;
  isFromCheckout?: boolean;
  isFull?: boolean;
  invoiceType?: string | Nullish;
}

const mapInvoiceTypeToInvoicingDocumentType = (
  invoiceType?: string | null
): InvoicingDocumentType => {
  switch (invoiceType) {
    case 'ISSUED':
      return 'invoice';
    case 'BALANCE_INVOICE':
      return 'balance_invoice';
    case 'PROFORMA':
      return 'invoice_proforma';
    case 'TAX_DOCUMENT_FOR_PAYMENT':
      return 'tax_document_for_payment';
    default:
      return 'invoice';
  }
};

export function CreateCorrectiveTaxDocumentForm(props: CreateCorrectiveTaxDocumentFormProps) {
  const createRelatedDocumentForCalculation =
    (): RelatedDocumentForCalculationRequestBody | null => {
      if (!props.defaultValues.id || !props.isFull) {
        return null;
      }

      return {
        relatedDocumentId: props.defaultValues.id,
        relatedDocumentForCalculationType: 'document-for-full-correction',
        relatedDocumentInvoicingType: mapInvoiceTypeToInvoicingDocumentType(props.invoiceType),
      };
    };
  return (
    <Form<AccountingDocumentFormValues>
      defaultValues={props.defaultValues}
      onSubmit={props.handleFormSubmit}
      experimentalZodSchema={createCorrectiveTaxDocumentSchema}
    >
      {(control, formApi) => (
        <InvoiceVatRateProvider
          control={control}
          listItemType="invoicing-documents"
          date={
            props.dateOfTaxableSupply
              ? getApiDateString(parseISO(props.dateOfTaxableSupply))
              : undefined
          }
        >
          <VStack>
            <FormHeader
              actions={[
                {
                  type: 'form-button',
                  title: i18n.t('general.actions.create'),
                  control,
                  buttonType: 'submit',
                  'data-testid':
                    testIds.accounting.newCorrectiveTaxDocument('create-corrective-tax'),
                },
              ]}
              title={i18n.t('entity.correctiveTaxDocument.labels.newDocument')}
            />
            <Hide when={props.isFromCheckout}>
              <DocumentSeriesSelect
                control={control}
                documentType="corrective_tax_document"
                data-testid={testIds.accounting.newCorrectiveTaxDocument('create-corrective-tax')}
              />
            </Hide>

            <InvoicePage>
              <Card>
                <VStack spacing={4}>
                  <Show when={isNotNil(props.defaultValues)}>
                    <CustomerAndSupplier
                      control={control}
                      formApi={formApi}
                      supplier={props.supplier}
                      allowAdditionalCustomer
                      isCustomerFixed
                      data-testid={testIds.accounting.newCorrectiveTaxDocument(
                        'create-customer-and-supplier'
                      )}
                    />
                  </Show>
                  <PaymentDates
                    formApi={formApi}
                    control={control}
                    data-testid={testIds.accounting.newCorrectiveTaxDocument('payment-dates')}
                  />
                </VStack>
              </Card>

              <PaymentDetails
                control={control}
                formApi={formApi}
                data-testid={testIds.accounting.newCorrectiveTaxDocument('payment-details')}
                originBankAccount={props.defaultValues?.paymentInfo?.bankAccount}
                isCorrectiveTaxDocument
              />

              <CorrectiveTaxDocumentDetails
                control={control}
                formApi={formApi}
                data-testid={testIds.accounting.newCorrectiveTaxDocument('document-details')}
              />

              <ListOfItemsForm
                control={control}
                formApi={formApi}
                listItemType="invoices"
                isCorrectiveTaxDocument
                isDisabled={props.isListOfItemsDisabled}
                dateOfTaxableSupply={props.dateOfTaxableSupply}
                relatedDocumentForCalculation={createRelatedDocumentForCalculation()}
                data-testid={testIds.accounting.newCorrectiveTaxDocument('list-of-items')}
              />
              <PaymentsNotes
                control={control}
                data-testid={testIds.accounting.newCorrectiveTaxDocument('payments-notes')}
              />
            </InvoicePage>
          </VStack>
        </InvoiceVatRateProvider>
      )}
    </Form>
  );
}
